import { type PropsWithChildren, useCallback, useEffect, useMemo, useReducer, useState } from "react";

import { ConnectionContext, type ConnectionContextValue } from "./connection-context";
import {
  describeConnection,
  initialConnectionState,
  reduceConnectionState,
  STALE_AFTER_MS,
  type ConnectionState,
} from "./connection-status";

const STALENESS_POLL_MS = 30_000;

function browserIsOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export type ConnectionStatusProviderProps = PropsWithChildren<{
  /** Test seam. Production starts unverified, because nothing has answered yet. */
  initialState?: ConnectionState;
}>;

export function ConnectionStatusProvider({ children, initialState }: ConnectionStatusProviderProps) {
  const [state, dispatch] = useReducer(
    reduceConnectionState,
    initialState ?? initialConnectionState(browserIsOnline()),
  );
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const online = () => dispatch({ online: true, type: "browser-online-hint-changed" });
    const offline = () => dispatch({ online: false, type: "browser-online-hint-changed" });
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);

  // Only confirmed-reachable data can become stale while nothing else happens,
  // so that is the only state that needs a clock.
  const watchForStaleness = state.reachability === "reachable";
  useEffect(() => {
    if (!watchForStaleness) {
      return;
    }
    const timer = window.setInterval(() => setNow(new Date()), STALENESS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [watchForStaleness]);

  const reportApiOutcome = useCallback((outcome: "succeeded" | "failed") => {
    const at = new Date();
    setNow(at);
    dispatch(
      outcome === "succeeded"
        ? { at, type: "api-request-succeeded" }
        : { at, type: "api-request-failed" },
    );
  }, []);

  const value = useMemo<ConnectionContextValue>(
    () => ({ notice: describeConnection(state, now), reportApiOutcome, state }),
    [now, reportApiOutcome, state],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

export { STALE_AFTER_MS };
